import { useMemo, useState } from 'react'
import NextLink from 'next/link'
import clsx from 'clsx'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'

import {
  PRAKTIK_KINERJA_STEP_ORDER,
  PRAKTIK_KINERJA_STEPS,
} from 'app/KinerjaGpkPage/constants/praktik-kinerja-steps'
import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import useAppealRollback from 'app/KinerjaGpkPage/hooks/use-appeal-rollback'
import { PelaksanaanAppealRequestDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRequestDialog'
import {
  API_PATH_PELAKSANAAN_PROGRESS,
  API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT,
} from 'api/kinerja/useAPIKinerja'
import { PelaksanaanAppealRollbackDialog } from 'app/KinerjaGpkPage/components/PelaksanaanAppeal/PelaksanaanAppealRollbackDialog'

import type { StepCardStatus } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import type {
  TStatePelaksanaan,
  TFollowUpEvaluationDocumentStates,
  TPelaksanaanAppealableStep,
} from 'types/kinerja-pelaksanaan'

type RefleksiEvaluasiStepCardProps = {
  currentState?: TStatePelaksanaan
  appealState?: TPelaksanaanAppealableStep
  latestFormState?: TFollowUpEvaluationDocumentStates
}

const STEP: TStatePelaksanaan = 'DOCUMENT_REFLECTION_FOLLOW_UP'

/**
 * @deprecated Use `RefleksiEvaluasiStepCard` from
 * `app/KinerjaShared/components/PraktikKinerjaPegawaiStepCards` for new
 * implementations across roles.
 */
export function RefleksiEvaluasiStepCard({
  appealState,
  currentState,
  latestFormState,
}: RefleksiEvaluasiStepCardProps) {
  const queryClient = useQueryClient()
  const { isAppealRollbackEnabled } = useAppealRollback()

  // ----------------------------------------------------
  // Dialog state
  // ----------------------------------------------------
  const [activeDialog, setActiveDialog] = useState<
    'ROLLBACK' | 'APPEAL' | 'NONE'
  >('NONE')

  // ----------------------------------------------------
  // Resolve step card status
  // ----------------------------------------------------
  const stepCardStatus: StepCardStatus = useMemo(() => {
    const currentStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(currentState)
    const refleksiStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(STEP)

    const appealStepIndex = PRAKTIK_KINERJA_STEP_ORDER.indexOf(appealState)
    const isAppealingPriorSteps =
      appealState && appealStepIndex < refleksiStepIndex

    if (currentStepIndex > refleksiStepIndex) return 'completed'
    if (currentStepIndex === refleksiStepIndex && !isAppealingPriorSteps)
      return 'active'
    return 'locked'
  }, [currentState, appealState])

  // Action Button states
  const showActionButton = useMemo(
    () => ['active', 'completed'].includes(stepCardStatus),
    [stepCardStatus]
  )
  const actionLabel = useMemo(() => {
    if (stepCardStatus === 'active') {
      return latestFormState === 'DRAFT' ? 'Lanjutkan' : 'Lakukan'
    }
    return 'Cek rincian'
  }, [latestFormState, stepCardStatus])

  // ----------------------------------------------------
  // Step data
  // ----------------------------------------------------
  const stepData = PRAKTIK_KINERJA_STEPS[STEP]

  // ----------------------------------------------------
  // Other derived values
  // ----------------------------------------------------
  const isAppealing = appealState === STEP
  const showAppealCTA =
    isAppealRollbackEnabled && stepCardStatus === 'completed'
  const isAppealRollbackDisabled = !!appealState && appealState !== STEP

  // ----------------------------------------------------
  // Handlers
  // ----------------------------------------------------
  const handleClickEdit = () => {
    if (
      currentState === 'REFLECTION_FOLLOW_UP_EVALUATION' &&
      latestFormState === 'NOT_CREATED'
    ) {
      // Immediately show rollback confirmation dialog
      // If confirmed, the form will immediately be set to DRAFT state
      setActiveDialog('ROLLBACK')
    } else {
      setActiveDialog('APPEAL')
    }
  }

  return (
    <>
      <PraktikKinerjaStepCard
        className="max-w-96"
        actions={
          <div className="flex gap-2 flex-wrap">
            {showActionButton && (
              <NextLink href={stepData.getPath()} passHref>
                <Button
                  as="a"
                  size="lg"
                  color={stepCardStatus === 'active' ? 'blue' : 'white'}
                  className="!inline-flex"
                  type={undefined}
                >
                  {actionLabel}
                </Button>
              </NextLink>
            )}

            {showAppealCTA ? (
              isAppealing ? (
                <div className="flex gap-1 items-center py-3 animate-pulse">
                  <Icon
                    color="informational"
                    className="motion-safe:animate-spin"
                  >
                    hourglass_empty
                  </Icon>
                  <Text className="!text-blue-60" color="informational">
                    Perubahan sedang diajukan.
                  </Text>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="lg"
                  color="blue"
                  disabled={isAppealRollbackDisabled}
                  className={clsx(
                    'gap-1 !bg-white',
                    isAppealRollbackDisabled && '!text-blue-40'
                  )}
                  onClick={handleClickEdit}
                >
                  <Icon color="inherit">edit_outline</Icon>
                  Ubah
                </Button>
              )
            ) : null}
          </div>
        }
        iconSrc={stepData.imagePath}
        status={stepCardStatus}
        lockedHint="Menunggu aksi dari Tim Kinerja."
        title={
          <>
            Pembinaan Kinerja: <br /> Isi {stepData.title}
          </>
        }
        description="Diskusi dengan Tim Kinerja untuk isi capaian, tantangan, dan upaya perbaikan."
      />

      <PelaksanaanAppealRequestDialog
        isOpen={activeDialog === 'APPEAL'}
        currentStep={currentState}
        appealStep={STEP as TPelaksanaanAppealableStep}
        onClose={() => setActiveDialog('NONE')}
      />

      <PelaksanaanAppealRollbackDialog
        isOpen={activeDialog === 'ROLLBACK'}
        onClose={() => setActiveDialog('NONE')}
        onCancel={() => setActiveDialog('NONE')}
        step={STEP as TPelaksanaanAppealableStep}
        url={stepData.getPath()}
        onSuccess={() => {
          // Invalidate without auto-refetch to prevent double fetch
          // React Query will refetch when needed
          // and deduplicate if multiple components share the same query key
          queryClient.invalidateQueries(API_PATH_PELAKSANAAN_PROGRESS, {
            refetchActive: false,
            refetchInactive: false,
          })
          queryClient.invalidateQueries(
            API_PATH_GET_FOLLOW_UP_REFLECTION_DOCUMENT,
            {
              refetchActive: false,
              refetchInactive: false,
            }
          )
        }}
      />
    </>
  )
}
